import { AUDIENCE_CARD_TYPE, SelectedMergeAudienceLists } from '@/types/registration.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import { Activity, useMemo, useState } from 'react';
import MergeListModal from '../new-audiences/merge-lists-modal';
import AudienceConfirmRemove from '../audience-confirm-remove';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

interface AudienceBulkActionsProps {
  selectedPasscodeListIds: number[];
  selectedRegistrantLists: AUDIENCE_CARD_TYPE[];
  onClearSelectedLists: () => void;
  onDownload: () => void;
  onRemove: () => void;
  onCreateCampaign: () => void;
}

const AudienceBulkActions = ({
  selectedPasscodeListIds,
  selectedRegistrantLists,
  onClearSelectedLists,
  onDownload,
  onRemove,
  onCreateCampaign,
}: AudienceBulkActionsProps) => {
  const [isMergeListModalOpen, setIsMergeListModalOpen] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const totalSelected = selectedPasscodeListIds.length + selectedRegistrantLists.length;

  const mergeInitialLists = useMemo<SelectedMergeAudienceLists>(
    () => ({
      sourceAudienceLists: selectedPasscodeListIds,
      sourceRegistrantLists: selectedRegistrantLists,
    }),
    [selectedPasscodeListIds, selectedRegistrantLists]
  );

  const validateRemove = () => {
    if (selectedRegistrantLists.length) {
      return toastTrigger('Default lists may not be removed', {
        description: 'Registered, Attended and No shows may not be removed from their project.',
        type: EToastTypes.error,
        autoClose: 7000,
      });
    } else {
      setIsConfirmRemoveOpen(true);
    }
  };

  return (
    <Activity mode={totalSelected > 0 ? 'visible' : 'hidden'}>
      <Container
        className="flex flex-row padding-v-10 padding-h-16 radius-8 jc-space-between"
        background="var(--global-opacity-white-5)"
        width="992px"
        height="60px"
      >
        <Container className="S1 flex ai-center">
          {totalSelected > 0 ? `${totalSelected} list${totalSelected === 1 ? '' : 's'} selected` : null}
        </Container>
        <Container className="flex fa-row gap-8 no-style">
          <Button onClick={onClearSelectedLists}>
            <Text className="L2 flex ai-center gap-6">
              <Icon name={ICONS.CLOSE} height={11} width={11} />
              <span>Clear selection</span>
            </Text>
          </Button>
          <Button design={TButtonAndLinkDesigns.LocalBttnSec} onClick={() => setIsMergeListModalOpen(true)}>
            <Text className="L2 flex ai-center gap-6">
              <Icon name={ICONS.ADD_FILE_CREATE_NOTE_SCRIPT_DOCUMENT} height={11} width={11} />
              <span>Add to existing list</span>
            </Text>
          </Button>
          <Button design={TButtonAndLinkDesigns.LocalBttnSec} onClick={onDownload}>
            <Text className="L2 flex ai-center gap-6">
              <Icon name={ICONS.DOWNLOAD} height={11} width={11} />
              <span>Download</span>
            </Text>
          </Button>
          <Button design={TButtonAndLinkDesigns.LocalBttnSec} onClick={onCreateCampaign}>
            <Text className="L2 flex ai-center gap-6">
              <Icon name={ICONS.AI_SEND_MESSAGE} height={11} width={11} />
              <span>Create campaign</span>
            </Text>
          </Button>
          {selectedPasscodeListIds.length > 0 && (
            <Button
              color="var(--global-error-500)"
              design={TButtonAndLinkDesigns.LocalBttnSec}
              onClick={validateRemove}
            >
              <Text color="var(--global-error-500)" className="L2 flex ai-center gap-6">
                <Icon name={ICONS.DELETE_TRASH_BIN} height={11} width={11} />
                <span>Remove</span>
              </Text>
            </Button>
          )}
        </Container>
      </Container>
      <MergeListModal
        open={isMergeListModalOpen}
        setOpen={(open) => {
          setIsMergeListModalOpen(open);
          if (!open) onClearSelectedLists();
        }}
        initialSelectedMergeAudienceLists={mergeInitialLists}
      />
      <AudienceConfirmRemove
        open={isConfirmRemoveOpen}
        count={selectedPasscodeListIds.length}
        onClose={() => setIsConfirmRemoveOpen(false)}
        onConfirm={onRemove}
      />
    </Activity>
  );
};

export default AudienceBulkActions;
